/**
 * ODDSIS Analytics & GTM Tracking System
 * Handles all dataLayer events for GTM, GA4, and affiliate tracking.
 */

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

/**
 * Push structured event to GTM dataLayer
 */
export function trackEvent(eventName, eventData = {}) {
  const payload = {
    event: eventName,
    timestamp: new Date().toISOString(),
    page_url: window.location.pathname,
    page_language: document.documentElement.lang || 'en',
    ...eventData,
  };
  window.dataLayer.push(payload);

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[GTM]', eventName, eventData);
  }
}

// ── PAGE EVENTS ────────────────────────────────────

export function trackPageView(pagePath, pageTitle) {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

export function trackMatchView(matchId, league, teams) {
  trackEvent('match_view', {
    match_id: matchId,
    league,
    teams,
  });
}

export function trackLeagueView(leagueId, leagueName) {
  trackEvent('league_view', {
    league_id: leagueId,
    league_name: leagueName,
  });
}

// ── AFFILIATE EVENTS (CRITICAL) ────────────────────

export function trackBookmakerClick(bookmakerName, matchId = '', odds = '', source = '') {
  trackEvent('bookmaker_click', {
    bookmaker_name: bookmakerName,
    match_id: matchId,
    odds_value: odds,
    click_source: source,
    conversion_type: 'affiliate',
  });
}

export function trackAffiliateImpression(bookmakerName, position, page) {
  trackEvent('affiliate_impression', {
    bookmaker_name: bookmakerName,
    position,
    page,
  });
}

// ── USER INTERACTION EVENTS ────────────────────────

export function trackPredictionRead(matchId) {
  trackEvent('prediction_read', { match_id: matchId });
}

export function trackOddsCompareClick(matchId) {
  trackEvent('odds_compare_click', { match_id: matchId });
}

export function trackValueBetClick(matchId, value) {
  trackEvent('value_bet_click', { match_id: matchId, value_percentage: value });
}

export function trackSurebetClick(matchId, profit) {
  trackEvent('surebet_click', { match_id: matchId, profit_percentage: profit });
}

export function trackLanguageChange(language) {
  trackEvent('language_change', { language });
}

export function trackSearch(query, resultsCount) {
  trackEvent('search', { search_query: query, results_count: resultsCount });
}

export function trackFAQExpand(question) {
  trackEvent('faq_expand', { question });
}

// ── ENGAGEMENT EVENTS ──────────────────────────────

let scrollTracked = {};

export function initScrollTracking() {
  const thresholds = [25, 50, 75, 90];
  window.addEventListener('scroll', () => {
    const scrollPct = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    for (const t of thresholds) {
      const key = `${window.location.pathname}_${t}`;
      if (scrollPct >= t && !scrollTracked[key]) {
        scrollTracked[key] = true;
        trackEvent(`scroll_${t}`, { page: window.location.pathname, depth: t });
      }
    }
  }, { passive: true });
}

export function initTimeOnPage() {
  const intervals = [15, 30, 60, 120];
  for (const sec of intervals) {
    setTimeout(() => {
      trackEvent(`time_on_page_${sec}s`, { page: window.location.pathname, seconds: sec });
    }, sec * 1000);
  }
}

// ── AUTO-INIT ──────────────────────────────────────

if (typeof window !== 'undefined') {
  initScrollTracking();
  initTimeOnPage();
}
